import { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiBadgeDot from "components/SuiBadgeDot";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import config from "configs";
import { AdminUser } from "api";

var TITLE = "Internal Users";

var columns = [
  { Header: "LAST NAME", accessor: "lastName", width: "20%" },
  { Header: "FIRST NAME", accessor: "firstName", width: "20%" },
  { Header: "MOBILE NUMBER", accessor: "mobile" },
  { Header: "LOGIN EMAIL", accessor: "loginEmail" },
  { Header: "DEPARTMENT", accessor: "department" },
  { Header: "POSITION", accessor: "position" },
  {
    Header: "STATUS",
    accessor: "status",
    width: "15%",
    Cell: ({ value }) => {
      let color = "success";
      if (value === "inactive") {
        color = "dark";
      }
      return (
        <SuiBox ml={-1.325}>
          <SuiBadgeDot size="xs" color={color} badgeContent={value} />
        </SuiBox>
      );
    },
  },
];

function AdminUserList({mode}) {
  const { t } = useTranslation();
  TITLE = t("Admin Users");
  columns = [
    { Header: t("Last Name"), accessor: "lastName", width: "20%" },
    { Header: t("First Name"), accessor: "firstName", width: "20%" },
    { Header: t("Mobile Number"), accessor: "mobile" },
    { Header: t("Login Email"), accessor: "loginEmail" },
    { Header: t("Department"), accessor: "department", 
      Cell: ({ value }) => {      
        return (
          <SuiBox ml={-1.325}>
            {t(value)}
          </SuiBox>
        );
      },
    },
    { Header: t("Position"), accessor: "position", 
      Cell: ({ value }) => {      
        return (
          <SuiBox ml={-1.325}>
            {t(value)}
          </SuiBox>
        );
      },
    },
    {
      Header: t("Status"),
      accessor: "status",
      width: "15%",
      Cell: ({ value }) => {
        let color = "success";
        if (value === "inactive") {
          color = "dark";
        }
        return (
          <SuiBox ml={-1.325}>
            <SuiBadgeDot size="xs" color={color} badgeContent={t(value)} />
          </SuiBox>
        );
      },
    },
  ];
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [rawData, setRawData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const tableData = useMemo(() => {
    const data = { columns, rows: [] };

    data.rows = [];
    _.get(rawData, "results", []).forEach((result) => {
      data.rows.push({
        id: result.id,
        firstName: result.firstName,
        lastName: result.lastName,
        mobile: result.mobile,
        loginEmail: result.loginEmail,
        department: result.department,
        position: result.position,
        status: result.status,
        style: {
          cursor: 'pointer'
        },
      });
    });

    return data;
  }, [rawData]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const params = { page, pageSize: config.defaults.PAGE_SIZE };
        if (activeTab === 1) {
          params.status = "active";
        } else if (activeTab === 2) {
          params.status = "inactive";
        }
        const rsp = await AdminUser.getAll(params);
        const { data } = rsp;
        setRawData(data);

        const { total, pageSize } = data;
        setPageCount(Math.ceil(total / pageSize));
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    })();
  }, [page, activeTab]);

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5} width="100%">
          <SuiBox width="100%">
            <Card>
              <SuiBox p={3} lineHeight={1}>
                <SuiBox display="flex" justifyContent="space-between" alignItems="center" pt={2}>
                {mode !== "view" && (
                  <SuiButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      history.push(`/adminUsers/new`);
                    }}
                  >
                    {t("ADD USER")}
                  </SuiButton>
                )}
                {mode === "view" && <span />}
                  <SuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="50%"
                  >
                    <SuiBox width="100%">
                      <AppBar position="static">
                        <Tabs
                          orientation="horizontal"
                          value={activeTab}
                          onChange={(...args) => {
                            setActiveTab(args[1]);
                          }}
                        >
                          <Tab label={t("All")} style={{ padding: 10 }} />
                          <Tab label={t("Active")} />
                          <Tab label={t("Inactive")} />
                        </Tabs>
                      </AppBar>
                    </SuiBox>
                  </SuiBox>
                  <SuiBox />
                </SuiBox>
              </SuiBox>

              {isLoading ? (
                // Loading placeholder
                <SuiBox p={3}>Loading...</SuiBox>
              ) : (
                <DataTable
                  pageCount={pageCount}
                  table={tableData}
                  showTotalEntries={false}
                  entriesPerPage={false}
                  isSorted={true}
                  onRowClick={(row) => {
                    history.push(`/adminUsers/edit/${row.original.id}`);
                  }}
                  onPageChange={setPage}
                  page={page}
                />
              )}
            </Card>
          </SuiBox>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default AdminUserList;